'use client';

import type { WorkspacePrefillRequestV1 } from '@/cms/components/blocks/workspace/workspace-prefill-contract';
import { type ReactNode, createContext, useContext } from 'react';

export type CmsComponentButtonTriggerOptions = {
  workspacePrefill?: WorkspacePrefillRequestV1;
};

export type CmsResolvedComponentButton = {
  external?: boolean;
  href?: string | null;
  label: string;
  preload?: () => void;
  trigger: (options?: CmsComponentButtonTriggerOptions) => void;
};

type CmsComponentButtonResolver = (params: {
  blockInstanceId: string;
  buttonKey: string;
  repeatedItemId?: string;
}) => CmsResolvedComponentButton | null;

type CmsComponentButtonScopeValue = {
  blockInstanceId: string;
  repeatedItemIds: Record<string, string[]>;
};

export type CmsComponentButtonBinding =
  | { managed: false; button: null }
  | { managed: true; button: CmsResolvedComponentButton | null };

const CmsComponentButtonResolverContext =
  createContext<CmsComponentButtonResolver | null>(null);
const CmsComponentButtonScopeContext =
  createContext<CmsComponentButtonScopeValue | null>(null);

export function CmsComponentButtonActionProvider({
  children,
  resolveButton,
}: {
  children: ReactNode;
  resolveButton: CmsComponentButtonResolver;
}) {
  return (
    <CmsComponentButtonResolverContext.Provider value={resolveButton}>
      {children}
    </CmsComponentButtonResolverContext.Provider>
  );
}

export function CmsComponentButtonScope({
  blockInstanceId,
  repeatedItemIds = {},
  children,
}: {
  blockInstanceId: string;
  repeatedItemIds?: Record<string, string[]>;
  children: ReactNode;
}) {
  return (
    <CmsComponentButtonScopeContext.Provider
      value={{ blockInstanceId, repeatedItemIds }}
    >
      {children}
    </CmsComponentButtonScopeContext.Provider>
  );
}

const resolveUnboundPreviewButton: CmsComponentButtonResolver = () => null;

export function CmsComponentButtonPreviewScope({
  blockInstanceId,
  repeatedItemIds,
  children,
}: {
  blockInstanceId: string;
  repeatedItemIds?: Record<string, string[]>;
  children: ReactNode;
}) {
  return (
    <CmsComponentButtonActionProvider
      resolveButton={resolveUnboundPreviewButton}
    >
      <CmsComponentButtonScope
        blockInstanceId={blockInstanceId}
        repeatedItemIds={repeatedItemIds}
      >
        {children}
      </CmsComponentButtonScope>
    </CmsComponentButtonActionProvider>
  );
}

export function useCmsComponentButton(
  buttonKey: string,
  repeatedItem?: { collectionKey: string; itemIndex: number }
) {
  const resolveButton = useContext(CmsComponentButtonResolverContext);
  const scope = useContext(CmsComponentButtonScopeContext);
  return resolveCmsComponentButtonBinding({
    resolveButton,
    scope,
    buttonKey,
    repeatedItem,
  });
}

export function useCmsComponentBlockInstanceId() {
  const scope = useContext(CmsComponentButtonScopeContext);
  if (!scope) {
    throw new Error('CMS component scope is missing.');
  }

  return scope.blockInstanceId;
}

export function resolveCmsComponentButtonBinding(params: {
  resolveButton: CmsComponentButtonResolver | null;
  scope: CmsComponentButtonScopeValue | null;
  buttonKey: string;
  repeatedItem?: { collectionKey: string; itemIndex: number };
}): CmsComponentButtonBinding {
  if (!params.resolveButton && !params.scope) {
    return { managed: false, button: null };
  }
  if (!params.resolveButton || !params.scope) {
    throw new Error(
      'CMS component button runtime provider and component scope must be mounted together.'
    );
  }
  const repeatedItemId = params.repeatedItem
    ? params.scope.repeatedItemIds[params.repeatedItem.collectionKey]?.[
        params.repeatedItem.itemIndex
      ]
    : undefined;
  if (params.repeatedItem && !repeatedItemId) {
    return { managed: true, button: null };
  }
  return {
    managed: true,
    button: params.resolveButton({
      blockInstanceId: params.scope.blockInstanceId,
      buttonKey: params.buttonKey,
      repeatedItemId,
    }),
  };
}
